import { Check, Loader2 } from 'lucide-react';
import type { ChangeEvent, ReactNode } from 'react';
import { cn } from '../../../lib/utils';
import { INPUT_CLASSES } from '../../Input';
import { PackageNameFieldBase } from '../../shared/PackageNameFieldBase';

interface PackageNameFieldProps {
  value: string;
  error: string | null;
  isChecking: boolean;
  showSpinner: boolean;
  isValid: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

/** Trailing 16px status glyph inside the input; fades rather than popping. */
function TrailingIndicator({
  visible,
  children,
}: {
  visible: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'pointer-events-none absolute right-3 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center transition-opacity duration-150',
        visible ? 'opacity-100' : 'opacity-0',
      )}
    >
      {children}
    </span>
  );
}

export function PackageNameField({
  value,
  error,
  isChecking,
  showSpinner,
  isValid,
  onChange,
}: PackageNameFieldProps) {
  return (
    <PackageNameFieldBase
      value={value}
      required
      description="Enter the exact Android application ID from your APK (case-sensitive), e.g., com.example.app."
      error={error}
      showError={!isChecking}
      onChange={onChange}
      inputClassName={`${INPUT_CLASSES} pr-10`}
      afterInput={
        <>
          <TrailingIndicator visible={showSpinner}>
            <Loader2
              aria-label="Checking package name"
              className="size-4 animate-spin text-seeker-400"
            />
          </TrailingIndicator>
          <TrailingIndicator
            visible={!showSpinner && isValid && Boolean(value)}
          >
            <Check
              aria-label="Package name available"
              className="size-4 text-green-400"
              strokeWidth={2.5}
            />
          </TrailingIndicator>
        </>
      }
    />
  );
}
